import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { X, Loader2, Mail, RefreshCw, Lock, CheckCircle } from 'lucide-react'
import { api } from '../../services/api'
import { toast } from 'sonner'

interface PasswordResetModalProps {
  isOpen: boolean
  onClose: () => void
}

type ResetStep = 'email' | 'verify-code' | 'new-password' | 'success'

export default function PasswordResetModal({ isOpen, onClose }: PasswordResetModalProps) {
  const { t } = useTranslation(['passwordReset', 'common'])
  const [step, setStep] = useState<ResetStep>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const forgotPasswordMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.forgotPassword(email)
    },
    onSuccess: () => {
      toast.success('Verification code sent! Please check your email.')
      setStep('verify-code')
      setResendCooldown(60)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to send reset code'
      setError(errorMessage)
      console.error('Forgot password error:', error)
    },
  })

  const resendOTPMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.resendOTP(email, 'password-reset')
    },
    onSuccess: () => {
      toast.success('Verification code resent! Please check your email.')
      setResendCooldown(60)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to resend code'
      setError(errorMessage)
      console.error('Resend OTP error:', error)
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.resetPassword(email, code, newPassword)
    },
    onSuccess: () => {
      setStep('success')
      toast.success('Password reset successfully!')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to reset password'
      setError(errorMessage)
      console.error('Reset password error:', error)
    },
  })

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      toast.error('Please enter your email address')
      return
    }

    try {
      await forgotPasswordMutation.mutateAsync()
    } catch {
      // Error handled in onError
    }
  }

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!code || code.length !== 6) {
      toast.error('Please enter the 6-digit verification code')
      return
    }

    setStep('new-password')
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    try {
      await resetPasswordMutation.mutateAsync()
    } catch {
      // Error handled in onError
    }
  }

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return

    try {
      await resendOTPMutation.mutateAsync()
    } catch {
      // Error handled in onError
    }
  }

  const handleClose = () => {
    if (!forgotPasswordMutation.isPending && !resetPasswordMutation.isPending && !resendOTPMutation.isPending) {
      setEmail('')
      setCode('')
      setNewPassword('')
      setConfirmPassword('')
      setError('')
      setStep('email')
      setResendCooldown(0)
      onClose()
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setCode('')
    setError('')
  }

  const handleBackToCode = () => {
    setStep('verify-code')
    setNewPassword('')
    setConfirmPassword('')
    setError('')
  }

  if (!isOpen) return null

  const isPending = forgotPasswordMutation.isPending || resetPasswordMutation.isPending || resendOTPMutation.isPending

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-background-card rounded-xl max-w-md w-full border border-green-900/20 my-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-green-900/20">
          <h2 className="text-2xl font-bold text-white">
            {step === 'email' && 'Reset Password'}
            {step === 'verify-code' && 'Verify Code'}
            {step === 'new-password' && 'New Password'}
            {step === 'success' && 'Password Reset'}
          </h2>
          {!isPending && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <>
              <p className="text-gray-300 mb-6 text-center">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder="Enter your email"
                      disabled={isPending}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 text-center">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full btn-gold py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  {forgotPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    <>Send Reset Code</>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Step 2: Verify Code */}
          {step === 'verify-code' && (
            <>
              <div className="mb-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gold-500/10 rounded-full mb-4">
                  <Mail className="h-8 w-8 text-gold-400" />
                </div>
                <p className="text-gray-300 mb-2">
                  We've sent a verification code to:
                </p>
                <p className="text-white font-semibold mb-4">{email}</p>
                <p className="text-sm text-gray-400">
                  Enter the 6-digit code to continue
                </p>
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 text-center">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setCode(value)
                      setError('')
                    }}
                    className="w-full px-4 py-4 text-center text-2xl tracking-[0.5em] bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50 font-mono"
                    placeholder="000000"
                    disabled={isPending}
                    maxLength={6}
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Code expires in 15 minutes
                  </p>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 text-center">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isPending || code.length !== 6}
                  className="w-full btn-gold py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  Continue
                </button>

                {/* Resend Code */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || resendOTPMutation.isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${resendOTPMutation.isPending ? 'animate-spin' : ''}`} />
                    {resendCooldown > 0 ? (
                      `Resend code in ${resendCooldown}s`
                    ) : (
                      'Resend verification code'
                    )}
                  </button>
                </div>

                {/* Back Button */}
                <div className="text-center pt-4 border-t border-green-900/20">
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    disabled={isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50"
                  >
                    ← Back to email
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 'new-password' && (
            <>
              <p className="text-gray-300 mb-6 text-center">
                Enter your new password
              </p>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value)
                        setError('')
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder="Enter new password"
                      disabled={isPending}
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 6 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value)
                        setError('')
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder="Confirm new password"
                      disabled={isPending}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 text-center">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full btn-gold py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  {resetPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Resetting password...
                    </>
                  ) : (
                    <>Reset Password</>
                  )}
                </button>

                {/* Back Button */}
                <div className="text-center pt-4 border-t border-green-900/20">
                  <button
                    type="button"
                    onClick={handleBackToCode}
                    disabled={isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50"
                  >
                    ← Back to verification
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <>
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-6">
                  <CheckCircle className="h-12 w-12 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Password Reset Successful!</h3>
                <p className="text-gray-300 mb-8">
                  Your password has been reset. You can log in with your new password. If you haven't verified your email yet, you'll be prompted to verify it when you sign in.
                </p>

                <button
                  onClick={handleClose}
                  className="w-full btn-gold py-3 rounded-lg font-semibold"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
